import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { CountryService } from '../../../../core/services/country.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Country } from '../../../../core/models/country';
import { RegexConstants } from '../../../../core/constants/regex-constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';

@Component({
  selector: 'em-country-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, ToastModule, TranslatePipe, RequiredMarkDirective],
  templateUrl: './country-form.component.html',
  styleUrl: './country-form.component.scss',
  providers: [MessageService],
})
export class CountryFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  errorMessages: any = {};
  translations: any = {};
  edited: boolean = false;

  constructor(
    private fb: FormBuilder,
    private countryService: CountryService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.loadErrorMessages();
    this.form = this.fb.group({
      id: [null],
      name: [null, 
        Validators.compose([
        Validators.required,
        Validators.pattern(RegexConstants.COUNTRY_NAME)])],
      nameEs: [null,
        Validators.compose([
        Validators.required,
        Validators.pattern(RegexConstants.COUNTRY_NAME_ES)])],
      nameAr: [null,
        Validators.compose([
        Validators.required, 
        Validators.pattern(RegexConstants.COUNTRY_NAME_AR)])],
      code: [null,
         Validators.compose([
          Validators.required, 
          Validators.pattern(RegexConstants.COUNTRY_REGION_CODE)])],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.populateForm(this.config.data);
    }
    this.translate.onLangChange.subscribe(() => {
      this.loadErrorMessages();
    });
    this.form.valueChanges.subscribe(() => {
      this.edited = true;
    });
  }
  loadErrorMessages(): void {
    this.translate
      .get([
        'layout.configurations.country.countryValidators.required.nameEn',
        'layout.configurations.country.countryValidators.required.nameEs',
        'layout.configurations.country.countryValidators.required.nameAr',
        'layout.configurations.country.countryValidators.required.code',
        'layout.configurations.country.countryValidators.pattern.nameEn',
        'layout.configurations.country.countryValidators.pattern.nameEs',
        'layout.configurations.country.countryValidators.pattern.nameAr',
        'layout.configurations.country.countryValidators.pattern.code',
      ])
      .subscribe((translations) => {
        this.errorMessages = {
          required: {
            name: translations['layout.configurations.country.countryValidators.required.nameEn'],
            nameEs: translations['layout.configurations.country.countryValidators.required.nameEs'],
            nameAr: translations['layout.configurations.country.countryValidators.required.nameAr'],
            code: translations['layout.configurations.country.countryValidators.required.code'],
          },
          pattern: {
            name: translations['layout.configurations.country.countryValidators.pattern.nameEn'],
            nameEs: translations['layout.configurations.country.countryValidators.pattern.nameEs'],
            nameAr: translations['layout.configurations.country.countryValidators.pattern.nameAr'],
            code: translations['layout.configurations.country.countryValidators.pattern.code'],
          },
        };
      });
  }
  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.country.addCountry.errorMessage.alreadyExists',
        'layout.configurations.country.editCountry.errorMessage.alreadyExists',
        'layout.configurations.country.addCountry.successMessage.title',
        'layout.configurations.country.editCountry.successMessage.title',
        'layout.configurations.country.addCountry.errorMessage.title',
        'layout.configurations.country.editCountry.errorMessage.title',
        'layout.configurations.country.addCountry.successMessage.detail',
        'layout.configurations.country.editCountry.successMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(country: Country) {
    this.form.patchValue({
      id: country.id,
      name: country.name,
      nameEs: country.nameEs,
      nameAr: country.nameAr,
      code: country.code,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateCountry();
      this.edited = false;
    } else {
      this.addCountry();
    }
  }

  addCountry() {
    this.countryService.addCountry(this.form.value).subscribe({
      next: (country: Country) => {
        this.ref.close(country);
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while saving the country: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.country.addCountry.errorMessage.title'],
          detail: this.translations['layout.configurations.country.addCountry.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateCountry() {
    if (!this.edited) {
      this.ref.close({ war: 'warning' });
      return;
    }
    this.countryService.updateCountry(this.form.value).subscribe({
      next: (country: Country) => {
        this.ref.close({ succes: 'succes' });
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while updating the country: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.country.editCountry.errorMessage.title'],
          detail: this.translations['layout.configurations.country.editCountry.errorMessage.alreadyExists'],
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);

    return {
      invalid: control?.invalid && (control?.dirty || control?.touched),
      message:
        control?.errors &&
        (control.errors['required']
           ? this.errorMessages.required[key]
           : control.errors['minlength']
          ? this.errorMessages.minLength[key]
           : this.errorMessages.pattern[key]),
    };
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field == field)?.errorMessage || '';
  }
}
